import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

interface SwapSettingsProps {
  slippage: number;
  deadline: number;
  onSlippageChange: (slippage: number) => void;
  onDeadlineChange: (deadline: number) => void;
}

const presetSlippages = [0.1, 0.5, 1.0];

export function SwapSettings({
  slippage,
  deadline,
  onSlippageChange,
  onDeadlineChange,
}: SwapSettingsProps) {
  return (
    <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Swap Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Slippage Tolerance */}
        <div className="space-y-2">
          <Label>Slippage Tolerance (%)</Label>
          <div className="flex items-center gap-2">
            {presetSlippages.map((preset) => (
              <Button
                key={preset}
                variant={slippage === preset ? "default" : "outline"}
                size="sm"
                onClick={() => onSlippageChange(preset)}
                className="flex-1"
              >
                {preset}%
              </Button>
            ))}
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={slippage}
                onChange={(e) => onSlippageChange(parseFloat(e.target.value) || 0)}
                className="w-20 h-8"
                step="0.1"
                min="0"
                max="50"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Higher slippage tolerance may result in unfavorable rates but reduces failed transactions.
          </p>
        </div>

        {/* Transaction Deadline */}
        <div className="space-y-2">
          <Label>Transaction Deadline (minutes)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={deadline}
              onChange={(e) => onDeadlineChange(parseInt(e.target.value) || 20)}
              className="w-24"
              min="1"
              max="60"
            />
            <span className="text-sm text-slate-500 dark:text-slate-400">minutes</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Your transaction will revert if it is pending for longer than this period.
          </p>
        </div>

        {/* MEV Protection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>MEV Protection</Label>
            <Button variant="outline" size="sm" disabled>
              Coming Soon
            </Button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Protect your trades from Maximal Extractable Value (MEV) attacks.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}